import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faBell, faClipboardList, faFileAlt, faShieldAlt, faUsers, faAward } from '@fortawesome/free-solid-svg-icons';

export default function TwoEyesWireframe() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const tabs = [
    { id: "dashboard", label: "Public Dashboard", icon: faEye },
    { id: "log", label: "Observation Log", icon: faClipboardList },
    { id: "alerts", label: "Alerts", icon: faBell },
    { id: "reports", label: "Reports", icon: faFileAlt },
    { id: "checklist", label: "Checklist & To-Do", icon: faShieldAlt },
    { id: "stakeholders", label: "Stakeholders", icon: faUsers },
    { id: "awards", label: "Vigilent Awards Leaderboard", icon: faAward },    
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-bold text-yellow-200">👀 TwoEyes</div>
          <div className="text-[10px] text-gray-200">Human-first observation layer for Social Alertness</div>
        </div>
        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-8 h-8 rounded-md bg-red-600 hover:bg-red-700 flex items-center justify-center">⏻</button>
            </>
          ) : (
            <button onClick={() => navigate("/login")} className="px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800">🔐</button>
          )}
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden w-8 h-8 text-2xl text-teal-300">{isMobileMenuOpen ? '×' : '☰'}</button>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs font-medium">{userName}</div>
              <button onClick={handleLogout} className="p-2 rounded-md bg-red-600 hover:bg-red-700">⏻</button>
            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">🔐</Link>
            </div>
          )}
        </div>
      )}

      <div className="text-white bg-black min-h-[80vh] mt-2 mb-2">
        {/* Tabs Grid */}
        <div className="grid grid-cols-4 lg:grid-cols-7 gap-1 p-1 place-items-center">
            {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border ${activeTab === tab.id ? 'bg-blue-800 text-white' : 'bg-blue-600 border-gray-300 text-gray-300'} hover:bg-blue-800 hover:text-white transition` }>
                    <FontAwesomeIcon icon={tab.icon} className="text-2xl" />
                    <span className="text-xs text-center mt-1">{tab.label}</span>
                </button>
            ))}
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
            {activeTab === 'dashboard' && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-800 shadow rounded p-4">
                    <h2 className="font-semibold text-white">Current Status</h2>
                    <p className="text-green-600 text-xl mt-2">🟢 All Normal</p>
                </div>
                <div className="bg-gray-800 shadow rounded p-4">
                    <h2 className="font-semibold text-white">Observations (24h)</h2>
                    <p className="text-cyan-400 text-2xl mt-2">18</p>
                </div>
                <div className="bg-gray-800 shadow rounded p-4">
                    <h2 className="font-semibold text-white">Alerts Raised</h2>
                    <p className="text-yellow-600 text-2xl mt-2">2</p>
                </div>
            </div>
            )}

            {activeTab === 'log' && (
            <div className="bg-gray-800 shadow rounded p-4 space-y-3">
                <h2 className="font-semibold text-lg lg:text-xl text-yellow-200">Recent Observations</h2>
                <ul className="text-sm [&_li]:text-white space-y-1">
                <li>🕒 10:30 PM – Unknown vehicle parked unusually long</li>
                <li>🕒 9:15 PM – Lift stopped between floors briefly</li>
                <li>🕒 6:00 PM – Repeated entry attempts at back gate</li>
                </ul>
                <button className="w-64 text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Add New Observation</button>
            </div>
            )}

            {activeTab === 'alerts' && (
            <div className="bg-gray-800 shadow rounded p-4 space-y-2">
                <h2 className="font-semibold text-lg lg:text-xl text-yellow-200">Active Alerts</h2>
                <p className="text-gray-200">⚠️ Back Gate access anomaly – Acknowledged</p>
                <p className="text-gray-200">⚠️ Basement movement after hours – Pending</p>
            </div>
            )}

            {activeTab === 'reports' && (
            <div className="bg-gray-800 shadow rounded p-4 space-y-2">
                <h2 className="font-semibold text-lg lg:text-xl text-yellow-200">Auto Reports</h2>
                <p className="text-gray-200">📄 Weekly Safety Summary</p>
                <p className="text-gray-200">📄 Repeated Observation Patterns</p>
                <p className="text-gray-200">📄 Missed Checklist Analysis</p>
            </div>
            )}

            {activeTab === 'checklist' && (
            <div className="bg-gray-800 shadow rounded p-4 space-y-2">
                <h2 className="font-semibold text-lg lg:text-xl text-yellow-200">Shift Checklist</h2>
                <ul className="space-y-1 [&_li]:text-white">
                <li>✅ Main Gate Lock Check</li>
                <li>✅ Visitor Register Updated</li>
                <li>❌ Fire Exit Patrol</li>
                </ul>
            </div>
            )}

            {activeTab === 'stakeholders' && (
            <div className="bg-gray-800 shadow rounded p-4 space-y-2">
                <h2 className="font-semibold text-lg lg:text-xl text-yellow-200">Stakeholders</h2>
                <ul className="list-disc pl-5 [&_li]:text-gray-100 space-y-1">
                <li>Security Guards / Watchmen</li>
                <li>Facility Managers</li>
                <li>Residents / Admin Committees</li>
                <li>Housekeeping & Support Staff</li>
                <li>Public Safety Authorities (View‑only)</li>
                </ul>
            </div>
            )}

            {activeTab === 'awards' && (
                <div className="bg-gray-800 shadow rounded p-4 space-y-4">
                    <h2 className="font-semibold text-lg lg:text-xl text-yellow-200">Most Vigilant Awards Leaderboard</h2>
                    <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                        <thead className="bg-gray-700">
                        <tr>
                            <th className="px-1 py-1 border">SN</th>
                            <th className="px-1 py-1 border">Name</th>
                            <th className="px-1 py-1 border">Place</th>
                            <th className="px-1 py-1 border">Count This Month</th>
                        </tr>
                        </thead>
                        <tbody>
                        {[
                            { sn: 1, name: 'Rahul Sharma', place: 'Sector 12', count: 15 },
                            { sn: 2, name: 'Neha Verma', place: 'Sector 7', count: 13 },
                            { sn: 3, name: 'Amit Singh', place: 'Sector 3', count: 12 },
                            { sn: 4, name: 'Priya Reddy', place: 'Sector 5', count: 10 },
                            { sn: 5, name: 'Vikram Das', place: 'Sector 8', count: 9 }
                        ].map((record) => (
                            <tr key={record.sn} className="text-center border-b">
                            <td className="px-4 py-2 border">{record.sn}</td>
                            <td className="px-4 py-2 border">{record.name}</td>
                            <td className="px-4 py-2 border">{record.place}</td>
                            <td className="px-4 py-2 border">{record.count}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}